import { useState, useRef, useCallback } from 'react';

/**
 * Hook لإدارة حالة مشغل الفيديو
 */
export const useVideoPlayer = (videoRef) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef(null);

  const togglePlayPause = useCallback(() => {
    if (!videoRef?.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(err => {
        // Video play error handling
      });
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, videoRef]);

  const handleVolumeChange = useCallback((newVolume) => {
    if (!videoRef?.current) return;
    const volumeValue = Math.max(0, Math.min(1, newVolume));
    videoRef.current.volume = volumeValue;
    setVolume(volumeValue);
  }, [videoRef]);

  const handleSeek = useCallback((time) => {
    if (!videoRef?.current) return;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  }, [videoRef]);

  const skipForward = useCallback(() => {
    if (!videoRef?.current) return;
    const newTime = Math.min(videoRef.current.currentTime + 10, duration);
    handleSeek(newTime);
  }, [videoRef, duration, handleSeek]);

  const skipBackward = useCallback(() => {
    if (!videoRef?.current) return;
    const newTime = Math.max(videoRef.current.currentTime - 10, 0);
    handleSeek(newTime);
  }, [videoRef, handleSeek]);

  const changePlaybackRate = useCallback((rate) => {
    if (!videoRef?.current) return;
    videoRef.current.playbackRate = rate;
    setPlaybackRate(rate);
  }, [videoRef]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      videoRef?.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, [videoRef]);

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  }, [isPlaying]);

  const handleTimeUpdate = useCallback((e) => {
    setCurrentTime(e.target.currentTime);
  }, []);

  const handleLoadedMetadata = useCallback((e) => {
    setDuration(e.target.duration);
  }, []);

  return {
    // State
    isPlaying,
    volume,
    currentTime,
    duration,
    playbackRate,
    isFullscreen,
    showControls,
    // Actions
    togglePlayPause,
    handleVolumeChange,
    handleSeek,
    skipForward,
    skipBackward,
    changePlaybackRate,
    toggleFullscreen,
    showControlsTemporarily,
    handleTimeUpdate,
    handleLoadedMetadata,
    setIsPlaying,
    setShowControls,
  };
};

/**
 * Hook لإدارة keyboard shortcuts
 */
export const useKeyboardShortcuts = (videoControls) => {
  const handleKeyPress = useCallback((e) => {
    // تجنب التعارض مع inputs
    if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
      return;
    }

    switch (e.key.toLowerCase()) {
      case ' ':
      case 'k':
        e.preventDefault();
        videoControls.togglePlayPause();
        break;
      case 'arrowleft':
        e.preventDefault();
        videoControls.skipBackward();
        break;
      case 'arrowright':
        e.preventDefault();
        videoControls.skipForward();
        break;
      case 'arrowup':
        e.preventDefault();
        videoControls.handleVolumeChange(videoControls.volume + 0.1);
        break;
      case 'arrowdown':
        e.preventDefault();
        videoControls.handleVolumeChange(videoControls.volume - 0.1);
        break;
      case 'm':
        e.preventDefault();
        videoControls.handleVolumeChange(videoControls.volume > 0 ? 0 : 1);
        break;
      case 'f':
        e.preventDefault();
        videoControls.toggleFullscreen();
        break;
      default:
        break;
    }
  }, [videoControls]);

  return { handleKeyPress };
};
